<template>
  <Modal
    actions
    name="UPDATE_DISPLACEMENT_APPOINTMENT_MODAL"
    :title="$t('appointment.adminActions.updateDisplacement.action')"
    ref="updateAppointmentDisplacementModal"
  >
    <template v-slot:body>
      <v-progress-linear
        v-if="isAppointmentLoading"
        class="mt-5 mb-7"
        indeterminate
        rounded
      />

      <div v-show="!isAppointmentLoading">
        <AppointmentFormDetails :appointment="appointment" />

        <v-form ref="appointmentUpdateDisplacement" lazy-validation>
          <v-text-field
            v-if="!isAppointmentLoading"
            v-model.lazy="displacement"
            v-money="catalogCurrency()"
            :label="$t('appointment.reschedule.displacement')"
            :error-messages="
              errorDisplacementComparison
                ? $t('appointment.reschedule.cantBeLess', { value: currentDisplacement.toFixed(2) })
                : ''
            "
            :error="errorDisplacementComparison"
            class="amount amount--small inputAmount"
            hide-details="auto"
            outlined
          />
        </v-form>
      </div>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="validate"
        :loading="loading"
        :disabled="disabled"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('appointment.adminActions.updateDisplacement.action') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { VMoney } from 'v-money'

import Modal from '@/components/app/Modal'

import AppointmentFormDetails from './formFieldsReschedule/AppointmentFormDetails'

import { catalogCurrency } from '@/util/catalog'
import { clearCurrency, checkCurrencyDecimals, toAmount } from '@/util/helpers'

export default {
  name: 'AppointmentUpdateDisplacement',

  components: {
    Modal,
    AppointmentFormDetails,
  },

  directives: {
    money: VMoney
  },

  data() {
    return {
      loading: false,
      disabled: false,
      displacement: 0,
      displacementComparison: 0,
      errorDisplacementComparison: false,
      catalogCurrency,
    }
  },

  computed: {
    ...mapGetters(['appointment', 'isAppointmentLoading']),

    currentDisplacement() {
      return toAmount(this.displacementComparison)
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },
  },

  watch: {
    appointment: {
      handler({ additionals }) {
        this.checkDisplacement(additionals)
        this.errorDisplacementComparison = false
      },

      deep: true,
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions([
      'updateAppointmentDisplacement',
      'updateAppointmentDisplacementAsAdmin',
    ]),

    checkDisplacement(additionals) {
      if (!additionals || additionals?.length <= 0) {
        this.displacement = 0
        this.displacementComparison = 0
        return
      }

      const displacement = checkCurrencyDecimals(additionals[0].value)

      this.displacement = displacement
      this.displacementComparison = displacement
    },

    // Com a visita já agendada o host contou com o valor combinado: o adicional só pode subir.
    hasErrorOnDisplacement(parsedDisplacement) {
      if (this.appointment.status !== 'SCHEDULED') {
        this.errorDisplacementComparison = false
        return false
      }

      // Comparar as strings do toFixed(2) inverte a ordem ('18.00' < '9.00') e o
      // adicional atual zerado chega como undefined, virando 'NaN': nesse caso
      // qualquer valor novo era barrado.
      this.errorDisplacementComparison =
        toAmount(parsedDisplacement) < this.currentDisplacement

      return this.errorDisplacementComparison
    },

    validate() {
      if (!this.$refs.appointmentUpdateDisplacement.validate()) return

      const parsedDisplacement = parseFloat(clearCurrency(this.displacement) ?? 0).toFixed(2)
      if (this.hasErrorOnDisplacement(parsedDisplacement)) return

      this.loading = true

      const payload = {
        id: this.appointment.id,
        displacement: parsedDisplacement,
      }

      Promise.resolve(
        this.isAdminOrPartner
          ? this.updateAppointmentDisplacementAsAdmin(payload)
          : this.updateAppointmentDisplacement(payload)
      )
        .then(() => {
          if (this.$parent?.refreshMap) this.$parent?.refreshMap()
          if (this.$parent?.refreshAppointments) this.$parent?.refreshAppointments()
        })
        .then(() => this.closeAllModals())
        .finally(() => this.loading = false)
    },
  },
}
</script>
